import { useState, lazy, Suspense, useCallback, useTransition, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import {
  Box,
  Container,
  Heading,
  Text,
  Progress,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  IconButton,
  Flex,
  useColorMode,
  Button,
  Spacer,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  Spinner,
  Icon,
} from '@chakra-ui/react'
import { MoonIcon, SunIcon, DownloadIcon, ChevronDownIcon, ChevronUpIcon, HamburgerIcon, WarningTwoIcon } from '@chakra-ui/icons'
import { useVulnerabilityData } from './hooks/useVulnerabilityData'
import { usePreferences } from './hooks/usePreferences'
import { useUrlState } from './hooks/useUrlState'
import { useWorkerFilter } from './hooks/useWorkerFilter'
import { VulnerabilityTable, ComparisonModal, AnalysisBreakdown, getFilterLabel, SearchWithSuggestions, ChartSkeleton, FilterSkeleton, InsightsSkeleton, ErrorBoundary, type ColumnFilter, type RowDensity } from './components'
import { exportToCSV, exportToJSON, getExportFilename } from './utils/export'
import { STRINGS } from './resources'
import { colors } from './theme'

// Lazy load charts for code splitting
const Charts = lazy(() =>
  import('./components/Charts').then((module) => ({ default: module.Charts }))
)
const RiskFactorsChart = lazy(() =>
  import('./components/RiskFactorsChart').then((module) => ({ default: module.RiskFactorsChart }))
)
const FilterBuilder = lazy(() =>
  import('./components/FilterBuilder').then((module) => ({ default: module.FilterBuilder }))
)

// Mobile detection
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || window.innerWidth < 768
}

// Mobile splash page
function MobileSplash() {
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={6}>
      <VStack
        bg={cardBg}
        p={8}
        borderRadius="xl"
        shadow="lg"
        maxW="400px"
        textAlign="center"
        spacing={6}
      >
        <Icon as={WarningTwoIcon} boxSize={12} color="orange.400" />
        <Heading size="lg">Desktop Required</Heading>
        <Text color="gray.500">
          This dashboard analyzes 236,000+ security vulnerabilities from a 371MB dataset.
          It requires a desktop browser with at least 4GB of available memory.
        </Text>
        <Text fontSize="sm" color="gray.400">
          Mobile browsers cannot handle datasets of this size and will crash.
        </Text>
        <Box pt={4} borderTop="1px" borderColor="gray.200" w="full">
          <Text fontSize="xs" color="gray.400">
            For a production app, we would use a backend API with pagination.
            This is a demonstration of client-side data processing at scale.
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}

// Main dashboard content component
function DashboardContent() {

  const { colorMode, toggleColorMode } = useColorMode()
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')
  const selectedBg = useColorModeValue('blue.50', 'blue.900')
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600')
  const { data, stats, isLoading, progress, progressMessage, error, worker } =
    useVulnerabilityData()

  // User preferences (persisted to localStorage)
  const { preferences, setPreferences } = usePreferences()

  // URL state for shareable links
  const { state: urlState, setState: setUrlState, clearAll: clearUrlState } = useUrlState()

  // Transition for non-blocking filter updates
  const [, startTransition] = useTransition()

  // Filter state - synced with URL for sharing
  const [searchInput, setSearchInput] = useState('') // Current input field value
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([])

  // Search pills from URL state
  const searchPills = urlState.searches

  // Add a search pill (on Enter)
  const addSearchPill = useCallback((term: string) => {
    const trimmed = term.trim()
    if (trimmed && !searchPills.includes(trimmed)) {
      setUrlState({ searches: [...searchPills, trimmed] })
    }
    setSearchInput('') // Clear input after adding
  }, [searchPills, setUrlState])

  // Remove a search pill
  const removeSearchPill = useCallback((term: string) => {
    setUrlState({ searches: searchPills.filter((s) => s !== term) })
  }, [searchPills, setUrlState])

  // Derived state from URL
  const excludeManualNoRisk = urlState.excludeManual
  const excludeAiNoRisk = urlState.excludeAi
  const severityFilters = urlState.severity
  const chartType = urlState.chartType
  const timeRange = urlState.timeRange
  const timeBucket = urlState.timeBucket
  const timeBucketLabel = urlState.timeBucketLabel

  // Web Worker filtering - uses shared worker, no duplicate data loading
  // Now includes time range filtering, stats, and chart data computation in the worker
  const { filteredData, filteredStats, legendCounts, chartData, isFiltering } = useWorkerFilter({
    worker,
    data,
    excludeManual: excludeManualNoRisk,
    excludeAi: excludeAiNoRisk,
    searchPills,
    severityFilters,
    columnFilters,
    timeRange,
    timeBucket,
  })

  // State setters that update URL (wrapped in transition for non-blocking updates)
  const setExcludeManualNoRisk = useCallback((val: boolean) => {
    startTransition(() => {
      setUrlState({ excludeManual: val })
    })
  }, [setUrlState])

  const setExcludeAiNoRisk = useCallback((val: boolean) => {
    startTransition(() => {
      setUrlState({ excludeAi: val })
    })
  }, [setUrlState])

  const setSeverityFilters = useCallback((val: string[] | ((prev: string[]) => string[])) => {
    startTransition(() => {
      if (typeof val === 'function') {
        setUrlState({ severity: val(urlState.severity) })
      } else {
        setUrlState({ severity: val })
      }
    })
  }, [setUrlState, urlState.severity])

  const setChartType = useCallback((val: 'bar' | 'area') => {
    setUrlState({ chartType: val })
  }, [setUrlState])

  const setTimeRange = useCallback((val: '7d' | '30d' | '90d' | '12m' | 'all') => {
    // Clear bucket filter when time range changes
    setUrlState({ timeRange: val, timeBucket: null, timeBucketLabel: null })
  }, [setUrlState])

  // Handle bucket click from chart (drill-down)
  const handleBucketClick = useCallback((bucketKey: string, bucketLabel: string) => {
    // Toggle - if clicking the same bucket, clear it
    if (timeBucket === bucketKey) {
      setUrlState({ timeBucket: null, timeBucketLabel: null })
    } else {
      setUrlState({ timeBucket: bucketKey, timeBucketLabel: bucketLabel })
    }
  }, [timeBucket, setUrlState])

  // Clear bucket filter
  const clearBucketFilter = useCallback(() => {
    setUrlState({ timeBucket: null, timeBucketLabel: null })
  }, [setUrlState])

  // Time range filtering and stats are now computed in the worker
  // filteredData already has time range and bucket filters applied
  // filteredStats contains the stats for the filtered data

  // Comparison state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isCompareOpen, setIsCompareOpen] = useState(false)

  const handleCompare = () => {
    if (selectedIds.size >= 2) {
      setIsCompareOpen(true)
    }
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
  }

  // Check if any filters are active
  const hasActiveFilters =
    excludeManualNoRisk ||
    excludeAiNoRisk ||
    searchPills.length > 0 ||
    columnFilters.length > 0 ||
    severityFilters.length > 0 ||
    !!timeBucket

  const clearAllFilters = () => {
    setSearchInput('')
    setColumnFilters([])
    clearUrlState()
    setPreferences({
      defaultExcludeManualNoRisk: false,
      defaultExcludeAiNoRisk: false,
    })
  }

  const handleSeverityClick = useCallback((severity: 'critical' | 'high' | 'medium' | 'low') => {
    setSeverityFilters((prev) => {
      return prev.includes(severity)
        ? prev.filter((s) => s !== severity)
        : [...prev, severity]
    })
  }, [setSeverityFilters])

  const handleAddRecentSearch = useCallback((search: string) => {
    if (!search.trim()) return
    setPreferences((prev) => ({
      recentSearches: [
        search,
        ...prev.recentSearches.filter((s: string) => s !== search),
      ].slice(0, 10),
    }))
  }, [setPreferences])

  // Get selected vulnerabilities for comparison - computed lazily only when modal opens
  // This avoids looping through all filteredData on every filter change
  const getSelectedVulnerabilities = useCallback(() => {
    return filteredData.filter((v) => selectedIds.has(v.id))
  }, [filteredData, selectedIds])

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Skip link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header */}
      <Box as="header" bg={cardBg} borderBottomWidth="1px" px={4} py={3} role="banner" className="header-gradient">
        <Flex justify="space-between" align="center" maxW="container.xl" mx="auto">
          <Heading as="h1" size="md">
            {STRINGS.APP.TITLE}
          </Heading>
          <IconButton
            aria-label={STRINGS.TOOLTIPS.TOGGLE_COLOR_MODE}
            icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
          />
        </Flex>
      </Box>

      <Container as="main" id="main-content" maxW="container.xl" py={6} role="main">
        {/* Loading State */}
        {isLoading && (
          <VStack spacing={4} py={20}>
            <Text fontSize="lg" fontWeight="medium">
              {progressMessage}
            </Text>
            <Box w="100%" maxW="400px">
              <Progress
                value={progress}
                size="lg"
                colorScheme="blue"
                borderRadius="full"
                hasStripe
                isAnimated
                aria-label={`Loading progress: ${Math.round(progress)}%`}
              />
            </Box>
            <Text color="gray.500" fontSize="sm">
              {STRINGS.APP.PROCESSING_MESSAGE}
            </Text>
          </VStack>
        )}

        {/* Error State */}
        {error && (
          <Box bg="red.50" p={4} borderRadius="md" color="red.700">
            <Text fontWeight="bold">{STRINGS.APP.ERROR_TITLE}</Text>
            <Text>{error}</Text>
          </Box>
        )}

        {/* Loaded State */}
        {!isLoading && !error && stats && (
          <VStack spacing={4} align="stretch">
            {/* Charts Row - Time series + Risk Factors side by side */}
            <Box>
              <Flex justify="flex-end" mb={preferences.chartsCollapsed ? 0 : 2}>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setPreferences({ chartsCollapsed: !preferences.chartsCollapsed })}
                  rightIcon={preferences.chartsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                >
                  {preferences.chartsCollapsed ? STRINGS.CHARTS_SECTION.SHOW : STRINGS.CHARTS_SECTION.HIDE}
                </Button>
              </Flex>
              <Box
                overflow="hidden"
                maxH={preferences.chartsCollapsed ? '0' : '500px'}
                opacity={preferences.chartsCollapsed ? 0 : 1}
                transition="all 0.3s ease"
              >
                <ErrorBoundary section="Charts">
                  <Suspense
                    fallback={
                      <Box bg={cardBg} borderRadius="md" shadow="sm">
                        <ChartSkeleton />
                      </Box>
                    }
                  >
                    <Box className="chart-container card-glow fade-in-up" bg={cardBg} borderRadius="lg" overflow="hidden" position="relative">
                      <Charts
                        data={filteredData}
                        chartData={chartData}
                        totalCount={data.length}
                        hasActiveFilters={hasActiveFilters}
                        severityFilters={severityFilters}
                        onSeverityClick={handleSeverityClick}
                        legendCounts={legendCounts ?? stats?.bySeverity ?? {}}
                        chartType={chartType}
                        timeRange={timeRange}
                        onChartTypeChange={setChartType}
                        onTimeRangeChange={setTimeRange}
                        onBucketClick={handleBucketClick}
                        selectedBucket={timeBucket}
                        isFiltering={isFiltering}
                      />
                    </Box>
                  </Suspense>
                </ErrorBoundary>
              </Box>
            </Box>

            {/* Filter Controls */}
            <Box>
              <Flex justify="flex-end" mb={preferences.filtersCollapsed ? 0 : 2}>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setPreferences({ filtersCollapsed: !preferences.filtersCollapsed })}
                  rightIcon={preferences.filtersCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                >
                  {preferences.filtersCollapsed ? STRINGS.FILTERS_SECTION.SHOW : STRINGS.FILTERS_SECTION.HIDE}
                </Button>
              </Flex>
              <Box
                overflow="hidden"
                maxH={preferences.filtersCollapsed ? '0' : '200px'}
                opacity={preferences.filtersCollapsed ? 0 : 1}
                transition="all 0.3s ease"
              >
                <Box bg={cardBg} p={4} borderRadius="md" shadow="sm" className="card-glow fade-in-up">
                  <VStack spacing={4} align="stretch">
                    {/* Search row */}
                    <Flex
                      direction={{ base: 'column', md: 'row' }}
                      gap={4}
                      align={{ base: 'stretch', md: 'center' }}
                    >
                      {/* Search input with button */}
                      <SearchWithSuggestions
                        value={searchInput}
                        onChange={setSearchInput}
                        onSubmit={addSearchPill}
                        data={data}
                        recentSearches={preferences.recentSearches}
                        onAddRecentSearch={handleAddRecentSearch}
                      />

                      <Spacer display={{ base: 'none', md: 'block' }} />

                      {/* Analysis Filter Buttons */}
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.500" fontWeight="medium">{STRINGS.FILTERS.HIDE_LABEL}</Text>
                        <Tooltip label={STRINGS.TOOLTIPS.HIDE_MANUAL} hasArrow>
                          <Button
                            size="sm"
                            bg={excludeManualNoRisk ? colors.filter.manual.active : 'transparent'}
                            color={excludeManualNoRisk ? 'white' : colors.filter.manual.inactive}
                            borderWidth="1px"
                            borderColor={excludeManualNoRisk ? colors.filter.manual.active : colors.filter.manual.inactive}
                            onClick={() => {
                              const newValue = !excludeManualNoRisk
                              setExcludeManualNoRisk(newValue)
                              setPreferences({ defaultExcludeManualNoRisk: newValue })
                            }}
                            leftIcon={
                              <Box
                                as="span"
                                display="inline-block"
                                w={2}
                                h={2}
                                borderRadius="full"
                                bg={excludeManualNoRisk ? 'white' : colors.filter.manual.inactive}
                                transition="all 0.2s"
                              />
                            }
                            transition="all 0.2s"
                            _hover={{ opacity: 0.9, transform: 'scale(1.02)' }}
                          >
                            {STRINGS.FILTERS.MANUAL}
                          </Button>
                        </Tooltip>
                        <Tooltip label={STRINGS.TOOLTIPS.HIDE_AI} hasArrow>
                          <Button
                            size="sm"
                            bg={excludeAiNoRisk ? colors.filter.ai.active : 'transparent'}
                            color={excludeAiNoRisk ? 'white' : colors.filter.ai.inactive}
                            borderWidth="1px"
                            borderColor={excludeAiNoRisk ? colors.filter.ai.active : colors.filter.ai.inactive}
                            onClick={() => {
                              const newValue = !excludeAiNoRisk
                              setExcludeAiNoRisk(newValue)
                              setPreferences({ defaultExcludeAiNoRisk: newValue })
                            }}
                            leftIcon={
                              <Box
                                as="span"
                                display="inline-block"
                                w={2}
                                h={2}
                                borderRadius="full"
                                bg={excludeAiNoRisk ? 'white' : colors.filter.ai.inactive}
                                transition="all 0.2s"
                              />
                            }
                            transition="all 0.2s"
                            _hover={{ opacity: 0.9, transform: 'scale(1.02)' }}
                          >
                            {STRINGS.FILTERS.AI}
                          </Button>
                        </Tooltip>
                      </HStack>
                    </Flex>

                    {/* Column Filters */}
                    <Suspense fallback={<FilterSkeleton />}>
                      <FilterBuilder filters={columnFilters} onFiltersChange={setColumnFilters} />
                    </Suspense>

                    {/* Active Filters */}
                    {hasActiveFilters && (
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={2}>
                          Active Filters
                        </Text>
                        <Box
                          maxH="68px"
                        overflowY="auto"
                        overflowX="hidden"
                        borderWidth="1px"
                        borderColor={filterBorderColor}
                        borderRadius="md"
                        p={2}
                        sx={{
                          '&::-webkit-scrollbar': { width: '6px' },
                          '&::-webkit-scrollbar-track': { background: 'transparent' },
                          '&::-webkit-scrollbar-thumb': { background: 'gray.400', borderRadius: '3px' },
                        }}
                      >
                        <Wrap spacing={2}>
                          {/* Search pills */}
                          {searchPills.map((term) => (
                            <Tag key={`search-${term}`} size="sm" colorScheme="green" borderRadius="md">
                              <TagLabel>Search: {term}</TagLabel>
                              <TagCloseButton onClick={() => removeSearchPill(term)} />
                            </Tag>
                          ))}
                          {/* Column filter pills */}
                          {columnFilters.map((filter) => (
                            <Tag key={filter.id} size="sm" colorScheme="blue" borderRadius="md">
                              <TagLabel>{getFilterLabel(filter)}</TagLabel>
                              <TagCloseButton onClick={() => setColumnFilters((prev) => prev.filter((f) => f.id !== filter.id))} />
                            </Tag>
                          ))}
                          {/* Severity filter pills */}
                          {severityFilters.map((sev) => (
                            <Tag key={sev} size="sm" bg={colors.severity[sev as keyof typeof colors.severity]} color="white" borderRadius="md">
                              <TagLabel textTransform="capitalize">{sev}</TagLabel>
                              <TagCloseButton onClick={() => setSeverityFilters((prev) => prev.filter((s) => s !== sev))} />
                            </Tag>
                          ))}
                          {/* Hide filter pills */}
                          {excludeManualNoRisk && (
                            <Tag size="sm" bg={colors.filter.manual.active} color="white" borderRadius="md">
                              <TagLabel>{STRINGS.FILTERS.MANUAL_PILL}</TagLabel>
                              <TagCloseButton onClick={() => {
                                setExcludeManualNoRisk(false)
                                setPreferences({ defaultExcludeManualNoRisk: false })
                              }} />
                            </Tag>
                          )}
                          {excludeAiNoRisk && (
                            <Tag size="sm" bg={colors.filter.ai.active} color="white" borderRadius="md">
                              <TagLabel>{STRINGS.FILTERS.AI_PILL}</TagLabel>
                              <TagCloseButton onClick={() => {
                                setExcludeAiNoRisk(false)
                                setPreferences({ defaultExcludeAiNoRisk: false })
                              }} />
                            </Tag>
                          )}
                          {/* Time bucket drill-down pill */}
                          {timeBucket && timeBucketLabel && (
                            <Tag size="sm" colorScheme="purple" borderRadius="md">
                              <TagLabel>{timeBucketLabel}</TagLabel>
                              <TagCloseButton onClick={clearBucketFilter} />
                            </Tag>
                          )}
                          {/* Clear all button */}
                          <Button size="xs" colorScheme="blue" variant="ghost" onClick={clearAllFilters}>
                            {STRINGS.FILTERS.CLEAR_ALL}
                          </Button>
                        </Wrap>
                      </Box>
                      </Box>
                    )}

                    {/* Screen reader announcement for filter changes */}
                    <Box className="sr-only" aria-live="polite" aria-atomic="true">
                      {hasActiveFilters
                        ? `Showing ${filteredData.length.toLocaleString()} of ${data.length.toLocaleString()} vulnerabilities`
                        : `Showing all ${data.length.toLocaleString()} vulnerabilities`}
                    </Box>
                  </VStack>
                </Box>
              </Box>
            </Box>

            {/* Vulnerability Table */}
            <Box as="section" bg={cardBg} p={4} borderRadius="md" shadow="sm" className="card-glow fade-in-up" aria-labelledby="table-heading">
              <HStack justify="space-between" mb={4}>
                <HStack spacing={4}>
                  <Heading as="h2" size="sm" id="table-heading">{STRINGS.TABLE.TITLE}</Heading>
                  {selectedIds.size > 0 && (
                    <>
                      <Text fontSize="sm" color="gray.500">
                        {selectedIds.size} {STRINGS.TABLE.SELECTED}
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        onClick={handleCompare}
                        isDisabled={selectedIds.size < 2}
                      >
                        {STRINGS.TABLE.COMPARE}
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="gray.500"
                        onClick={clearSelection}
                      >
                        {STRINGS.TABLE.CLEAR}
                      </Button>
                    </>
                  )}
                </HStack>
                <HStack spacing={3}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.500">
                      {filteredData.length.toLocaleString()} {STRINGS.TABLE.OF} {data.length.toLocaleString()}
                    </Text>
                    {isFiltering && <Spinner size="xs" color="blue.500" />}
                  </HStack>
                  {/* Row Density Selector */}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size="xs"
                      variant="ghost"
                      icon={<HamburgerIcon />}
                      aria-label="Row density"
                    />
                    <MenuList minW="120px">
                      {(['compact', 'normal', 'comfortable'] as RowDensity[]).map((density) => (
                        <MenuItem
                          key={density}
                          onClick={() => setPreferences({ tableRowDensity: density })}
                          fontWeight={preferences.tableRowDensity === density ? 'bold' : 'normal'}
                          bg={preferences.tableRowDensity === density ? selectedBg : undefined}
                        >
                          {STRINGS.ROW_DENSITY[density.toUpperCase() as keyof typeof STRINGS.ROW_DENSITY]}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                                    <Menu>
                    <MenuButton
                      as={Button}
                      size="xs"
                      variant="outline"
                      rightIcon={<ChevronDownIcon />}
                      leftIcon={<DownloadIcon />}
                    >
                      {STRINGS.EXPORT.BUTTON}
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        onClick={() => exportToCSV(filteredData, getExportFilename())}
                      >
                        {STRINGS.EXPORT.CSV}
                      </MenuItem>
                      <MenuItem
                        onClick={() => exportToJSON(filteredData, getExportFilename())}
                      >
                        {STRINGS.EXPORT.JSON}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </HStack>
              <ErrorBoundary section="Vulnerability Table">
                <VulnerabilityTable
                  data={filteredData}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  rowDensity={preferences.tableRowDensity}
                />
              </ErrorBoundary>
            </Box>

            {/* Collapsible Insights Section */}
            <Box>
              <Flex justify="flex-end" mb={preferences.insightsCollapsed ? 0 : 2}>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setPreferences({ insightsCollapsed: !preferences.insightsCollapsed })}
                  rightIcon={preferences.insightsCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                >
                  {preferences.insightsCollapsed ? STRINGS.INSIGHTS_SECTION.SHOW : STRINGS.INSIGHTS_SECTION.HIDE}
                </Button>
              </Flex>
              <Box
                overflow="hidden"
                maxH={preferences.insightsCollapsed ? '0' : '600px'}
                opacity={preferences.insightsCollapsed ? 0 : 1}
                transition="all 0.3s ease"
              >
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} className="fade-in-up">
                  <ErrorBoundary section="Risk Factors">
                    <Suspense
                      fallback={
                        <Box bg={cardBg} borderRadius="md" shadow="sm">
                          <InsightsSkeleton />
                        </Box>
                      }
                    >
                      <RiskFactorsChart riskFactorCounts={filteredStats?.byRiskFactor ?? {}} isFiltering={isFiltering} />
                    </Suspense>
                  </ErrorBoundary>

                  <ErrorBoundary section="Analysis Breakdown">
                    <Suspense
                      fallback={
                        <Box bg={cardBg} borderRadius="md" shadow="sm">
                          <InsightsSkeleton />
                        </Box>
                      }
                    >
                      <AnalysisBreakdown breakdown={filteredStats?.analysisBreakdown ?? null} isFiltering={isFiltering} />
                    </Suspense>
                  </ErrorBoundary>
                </SimpleGrid>
              </Box>
            </Box>
          </VStack>
        )}
      </Container>

      {/* Comparison Modal */}
      <ComparisonModal
        vulnerabilities={isCompareOpen ? getSelectedVulnerabilities() : []}
        isOpen={isCompareOpen}
        onClose={() => setIsCompareOpen(false)}
      />
    </Box>
  )
}

// App wrapper with routes
function App() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    setIsMobile(isMobileDevice())
  }, [])

  if (isMobile) {
    return <MobileSplash />
  }

  return (
    <Routes>
      <Route path="/" element={<DashboardContent />} />
    </Routes>
  )
}

export default App
